import { useEffect, useMemo, useState } from 'react';
import {
  Handshake, ArrowLeft, Pencil, Loader2, AlertCircle, Inbox, Copy, Check,
} from 'lucide-react';
import {
  listAgents, listAgentPayouts,
  getDisplayErrorMessage,
  type AgentRow,
} from '@/lib/api';
import { formatCommissionRow, type CommissionRow } from '@/lib/spCommissionMoney';
import { programRateLabel } from '@/lib/partnerDirectory';
import { usePartnerConnectStatus } from '@/hooks/usePartnerConnectStatus';
import { partnerConnectPresentation } from '@/lib/partnerConnectPresentation';
import { payoutLocale } from '@/lib/sitePayouts';
import { useAuthStore } from '@/store/auth';
import { AgentFormModal } from './AgentFormModal';
import { GoldenEnrolmentCard } from './GoldenEnrolmentCard';
import { AgentStatusBadge } from './AgentStatusBadge';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { PartnerNotes } from '@/pages/sales-team/PartnerNotes';
import styles from './AgentDetailPage.module.css';

/**
 * Per-agent AgentPayout history page (Phase 7e of the agent-commission
 * ticket). Final commit in the Phase 7 operator-surface arc.
 *
 * Header carries agent identity (name + email + Connect/clawback
 * status pills) plus an "Edit identity" button that opens the
 * existing AgentFormModal in edit mode (Phase 7a's modal stays the
 * canonical edit surface; this page hosts the data).
 *
 * Body is the full AgentPayout history table for the agent, with
 * status-chip filter and per-row details (transfer ID, refund
 * timestamps, last error). Read-only , Phase 7d's resolve action
 * is the only mutation surface; Phase 7d.1 will add retry / write-
 * off primitives once real ops feedback exists.
 *
 * Access:
 *   - super_admin: full view via the admin Agents rolodex →
 *     /agents/:id (nav-gated via the agents leaf's
 *     requiresSuperAdmin: true flag).
 *   - sales_agent (SP-self, Phase 7g.4b.1.5): the route's :id
 *     matches user.agent_id. SPs land here via the /payouts
 *     redirect in App.tsx (Phase 7g.4b.1). Server-side scope
 *     filter from 7g.4a allows the underlying
 *     /admin/agents/:id/payouts endpoint when id === their own
 *     linked agent_id. Edit identity button is already hidden
 *     for SP-self per 7g.4b.1; the rest of the page (commission
 *     history table, status pills, breadcrumb, copy-transfer-id)
 *     reads safely for SP-self.
 *   - Anyone else (deep-link from a non-super_admin non-SP user
 *     OR an SP probing a different agent's id): the role check
 *     short-circuits to an error state. Server-side gating on
 *     /admin/agents/:id/payouts is the authoritative deny.
 */

type FilterKey = 'all' | 'transferred' | 'pending_failed' | 'reversed' | 'clawback';

interface Props {
  agentId: string;
  onNavigate?: (to: string) => void;
}

export function AgentDetailPage({ agentId, onNavigate }: Props) {
  useRecentRoute('Sales Partner Detail');
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = user?.role === 'super_admin';
  // Phase 7g.4b.1.5 , SP-self viewer. SPs land here via the
  // /payouts redirect (App.tsx). The route id must match their
  // bound agent_id; any other id falls through to the access-
  // denied branch. Server-side gating on /admin/agents/:id/payouts
  // (Phase 7g.4a) is the authoritative deny if a malicious SP
  // hand-crafts a different id in the URL.
  const isSpSelf = !!user?.is_sales_agent && user?.agent_id === agentId;
  const isOperator = user?.role === 'operator';
  const canView = isSuperAdmin || isSpSelf || isOperator;

  const [agent, setAgent] = useState<AgentRow | null>(null);
  const [rows, setRows] = useState<CommissionRow[]>([]);
  // The partner's own view reads the live payout state, like Commissions.
  const connectStatus = usePartnerConnectStatus(agentId, isSpSelf);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [editOpen, setEditOpen] = useState(false);
  const [copiedTransferId, setCopiedTransferId] = useState<string | null>(null);

  // Two parallel fetches:
  //   1. listAgents() , picks our agent row out of the global list
  //      (no separate GET /admin/agents/:id endpoint by Phase 7a
  //      design; the list returns full Agent shape so the lookup
  //      is a Map filter)
  //   2. listAgentPayouts(agentId) , history feed
  //
  // The agent list fetch also gives us the rolodex sidebar context
  // for future expansion (prev/next agent navigation), though Phase
  // 7e doesn't surface that yet.
  const reload = async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      const [agents, history] = await Promise.all([
        listAgents(),
        (isOperator ? Promise.resolve(null) : listAgentPayouts(agentId)).catch((err: unknown) => {
          // History fetch can fail with 404 if the agent doesn't
          // exist. Don't propagate , the agent lookup below handles
          // the "not found" surface uniformly.
          if (err && typeof err === 'object' && 'response' in err) {
            const status = (err as { response?: { status?: number } }).response?.status;
            if (status === 404) return null;
          }
          throw err;
        }),
      ]);
      const matched = agents.find((a) => a.id === agentId) ?? null;
      setAgent(matched);
      if (history) {
        setRows(history.rows);
        setTruncated(history.truncated);
      } else {
        setRows([]);
        setTruncated(false);
      }
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Failed to load Sales Partner history'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      // Copy is intentionally generic ("not available") rather than
      // role-specific. A non-super_admin non-SP user hitting this
      // page is either a deep-link mistake (operator clicking a
      // stale link) or a probe attempt (SP trying a different
      // agent's id). Neither case wants to leak whether the role
      // gate or the id mismatch caused the deny.
      setError('This Sales Partner history is not available for your account.');
      return;
    }
    void reload();
    // reload is a stable closure on this component; intentionally
    // not in the dep array (would loop on every render). agentId
    // change drives reload via the route remount.
  }, [canView, agentId]);

  // Client-side filter chips. Server returns the full history; chips
  // slice locally per Phase 7d/7e convention.
  const visible = useMemo(() => {
    if (filter === 'all') return rows;
    if (filter === 'transferred') {
      // "Clean" transferred , fully paid out, no reversal activity.
      return rows.filter((r) =>
        r.status === 'transferred' && r.reversal_cents_cumulative === 0);
    }
    if (filter === 'pending_failed') {
      return rows.filter((r) =>
        r.status === 'pending'
        || r.status === 'pending_stripe_setup'
        || r.status === 'failed'
        || r.status === 'permanently_failed');
    }
    if (filter === 'reversed') {
      return rows.filter((r) =>
        r.status === 'reversed'
        || (r.status === 'transferred' && r.reversal_cents_cumulative > 0));
    }
    if (filter === 'clawback') {
      return rows.filter((r) =>
        r.status === 'clawback_pending' || r.status === 'absorbed_clawback');
    }
    return rows;
  }, [rows, filter]);

  const handleCopyTransferId = async (transferId: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(transferId);
      setCopiedTransferId(transferId);
      // Clear the visual confirmation after a short window so the
      // operator can confirm the copy fired without the indicator
      // sticking.
      setTimeout(() => setCopiedTransferId(null), 1500);
    } catch {
      // Clipboard access can fail in non-https contexts; the
      // transfer_id stays selectable as a fallback (display is
      // a <code> element).
    }
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.empty}>
          <Loader2 size={18} className={styles.spin} />
          <span>Loading Sales Partner history…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.backLink}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => onNavigate?.('/agents')}
          >
            <ArrowLeft size={14} /> Back to Sales Partners
          </button>
        </div>
        <div className={styles.error}>
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className={styles.page}>
        <div className={styles.backLink}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => onNavigate?.('/agents')}
          >
            <ArrowLeft size={14} /> Back to Sales Partners
          </button>
        </div>
        <div className={styles.empty}>
          <AlertCircle size={20} className={styles.emptyIcon} />
          <p className={styles.emptyTitle}>Sales Partner not found</p>
          <p className={styles.emptyBody}>
            This Sales Partner may have been deactivated and removed from the
            rolodex, or the deep link is stale.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.backLink}>
        <button
          type="button"
          className={styles.backBtn}
          onClick={() => onNavigate?.('/agents')}
        >
          <ArrowLeft size={14} /> Back to Sales Partners
        </button>
      </div>

      {(isSuperAdmin || isOperator) && <GoldenEnrolmentCard agentId={agentId} onChanged={() => void reload()} />}
      {(isSuperAdmin || isOperator) && <PartnerNotes kind="partner" id={agentId} />}
      {/* The program rate, not the stored reference default, which never pays (sales comp change, 27 October 2026). */}
      {agent.program_info && <p>Program rate: {programRateLabel(agent.program_info)}. Payouts use each charger's stamped rate.</p>}
      {agent?.debt_review_required && <p role="alert">Partner exit has outstanding commission debt. Operator review required; no invoice is sent automatically.</p>}
      <header className={styles.header}>
        <span className={styles.headerIcon}><Handshake size={20} /></span>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>
            {agent.name}
            {!agent.active && (
              <span className={styles.toggleHint}> (inactive)</span>
            )}
          </h1>
          <p className={styles.subtitle}>{agent.email}</p>
          <div className={styles.statusRow}>
            {isSpSelf ? (() => {
              const view = partnerConnectPresentation(connectStatus.data, !!agent.stripe_connect_account_id, payoutLocale());
              return <span className={view.ready ? styles.chipOk : styles.chipWarn}>{view.pill}</span>;
            })() : agent.stripe_connect_account_id ? (
              <span className={styles.chipOk}>✓ Connect ready</span>
            ) : (
              <span className={styles.chipWarn}>⚠ Connect pending</span>
            )}
            {agent.outstanding_clawback_cents > 0 && (
              <span className={styles.chipWarn}>
                ${(agent.outstanding_clawback_cents / 100).toFixed(2)} outstanding clawback
              </span>
            )}
          </div>
        </div>
        {/* Phase 7g.4b.1 , hide Edit identity for SP-self viewer.
            SPs land here via the /payouts redirect (their own Agent
            history). The full Edit identity modal exposes operator-
            only surfaces (commission rate, active toggle, country
            edit, invite controls) the SP shouldn't see. SP edits
            happen on the dedicated /me profile page, which ships
            in Phase 7g.4b.3 with a name+phone whitelist. Server-
            side PATCH /admin/agents/:id already enforces the
            whitelist for SP-self callers per 7g.4a, so this gate
            is the UX layer keeping operator-only controls hidden
            from the SP view. */}
        {isSuperAdmin && agent.can_edit !== false && (
          <button
            type="button"
            className={styles.editBtn}
            onClick={() => setEditOpen(true)}
          >
            <Pencil size={13} /> Edit identity
          </button>
        )}
      </header>

      {/* Filter chips */}
      <div className={styles.toolbar}>
        <div className={styles.filterChips}>
          {([
            { key: 'all', label: 'All' },
            { key: 'transferred', label: 'Transferred' },
            { key: 'pending_failed', label: 'Pending/Failed' },
            { key: 'reversed', label: 'Reversed' },
            { key: 'clawback', label: 'Clawback' },
          ] as const).map((f) => (
            <button
              key={f.key}
              type="button"
              className={`${styles.filterChip} ${filter === f.key ? styles.filterChipActive : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        {truncated && (
          <p className={styles.truncatedHint}>
            Showing first 200 rows (sorted newest first). Older history
            available via direct Mongo query for now , cursor pagination
            ships when this cap matters in production.
          </p>
        )}
      </div>

      {/* Empty / table */}
      {visible.length === 0 && rows.length === 0 ? (
        <div className={styles.empty}>
          <Inbox size={32} className={styles.emptyIcon} />
          <p className={styles.emptyTitle}>No payout history yet</p>
          <p className={styles.emptyBody}>
            Payout rows appear here once sessions on assigned
            chargers fire commissions. Phase 3e&rsquo;s
            <code> runAgentTransfer</code> writes the rows on every
            qualifying session-stop.
          </p>
        </div>
      ) : visible.length === 0 ? (
        <div className={styles.empty}>
          <p className={styles.emptyBody}>
            No payout rows match the current filter.
          </p>
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Session</th>
                {/* Phase 7g.4b.3 Commit 2 , Charger + Location columns
                    populated by the agent-payouts.ts projection extension.
                    Visible to super_admin (rolodex view) and SP-self
                    (Phase 7g.4b.1.5 self-view path) alike. */}
                <th>Charger</th>
                <th>Location</th>
                <th>Status</th>
                <th>Gross / Net</th>
                <th>Clawback / Reversed</th>
                <th>Transfer ID</th>
                <th>Last error</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.id}>
                  <td>
                    <span title={new Date(r.created_at).toLocaleString()}>
                      {new Date(r.created_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td>
                    <code className={styles.idCode} title={r.session_id}>
                      {r.session_started_at
                        ? new Date(r.session_started_at).toLocaleDateString()
                        : r.session_id.slice(-8)}
                    </code>
                  </td>
                  <td>
                    {r.charger_identity ?? <span className={styles.muted}>Not available</span>}
                  </td>
                  <td>
                    {r.location_name ?? <span className={styles.muted}>Not available</span>}
                  </td>
                  <td>
                    <AgentStatusBadge
                      status={r.status}
                      manualReview={r.manual_review_required}
                    />
                  </td>
                  <td>
                    <div className={styles.numStack}>
                      <span>{formatCommissionRow(r)}</span>
                      <span className={styles.muted}>
                        {formatCommissionRow(r, r.net_transfer_cents)} net
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className={styles.numStack}>
                      {r.reversal_cents_cumulative > 0 && (
                        <span className={styles.chipWarn}>
                          {formatCommissionRow(r, r.reversal_cents_cumulative)} reversed
                        </span>
                      )}
                      {r.clawback_debt_recorded_cents > 0 && (
                        <span className={styles.chipErr}>
                          {formatCommissionRow(r, r.clawback_debt_recorded_cents)} clawback
                        </span>
                      )}
                      {r.clawback_applied_cents > 0 && (
                        <span className={styles.muted}>
                          {formatCommissionRow(r, r.clawback_applied_cents)} netted at create
                        </span>
                      )}
                      {r.reversal_cents_cumulative === 0
                        && r.clawback_debt_recorded_cents === 0
                        && r.clawback_applied_cents === 0 && (
                        <span className={styles.muted}>Not available</span>
                      )}
                    </div>
                  </td>
                  <td>
                    {((): React.ReactNode => {
                      // Capture the transfer id into a local so TS
                      // narrows it inside the click handler closure
                      // (without this, the inline `!` triggers a
                      // forbidden-non-null-assertion lint warning).
                      const tid = r.stripe_transfer_id;
                      if (!tid) return <span className={styles.muted}>Not available</span>;
                      return (
                        <button
                          type="button"
                          className={styles.copyBtn}
                          onClick={() => void handleCopyTransferId(tid)}
                          title="Copy transfer ID"
                        >
                          <code className={styles.transferId}>
                            {tid.slice(0, 12)}…
                          </code>
                          {copiedTransferId === tid ? (
                            <Check size={11} className={styles.copyOk} />
                          ) : (
                            <Copy size={11} className={styles.copyIcon} />
                          )}
                        </button>
                      );
                    })()}
                  </td>
                  <td>
                    {r.last_error ? (
                      <span className={styles.errorCell} title={r.last_error}>
                        {r.last_error.length > 80
                          ? `${r.last_error.slice(0, 80)}…`
                          : r.last_error}
                      </span>
                    ) : (
                      <span className={styles.muted}>Not available</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit modal , same component the AgentsListPage uses */}
      {editOpen && (
        <AgentFormModal
          mode="edit"
          agent={agent}
          onClose={() => { setEditOpen(false); void reload(); }}
          onSaved={async () => {
            setEditOpen(false);
            await reload();
          }}
        />
      )}
    </div>
  );
}
